import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy } from "@utils/toolBox";
import { useSurveyStore } from "@stores/survey/survey";
import handleError from "@composables/handleError.js";
import { NotFoundError, ForbiddenError, UnauthorizedError } from "@routes/errors";
import { getClientId } from "@utils/clientId";
import { useDirectUpload } from "@hooks/useDirectUpload.js";
import { captureEvent } from "@/lib/analytics";

export const useMailingListStore = defineStore("mailingList", () => {
  // item
  const mailingList = ref({});
  const signedBlobId = ref(null);
  const initialMailingList = ref({});
  const isNewList = ref(false);
  const mailingListCsvFile = ref(null);

  // stores
  const { token } = storeToRefs(useSurveyStore());

  const hasListChanged = computed(
    () =>
      mailingList.value?.name !== initialMailingList.value?.name ||
      mailingList.value?.expired_at !== initialMailingList.value?.expired_at ||
      mailingList.value?.shared !== initialMailingList.value?.shared ||
      mailingListCsvFile.value != null,
  );

  const isListExpired = computed(() => {
    if (isNewList.value || !initialMailingList.value?.expired_at) return false;
    return Date.parse(initialMailingList.value.expired_at) < new Date();
  });

  const getMailingList = async (listToken) => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists/${listToken}`).json();
      if (response.value.status === 404) throw new NotFoundError("Page not found");
      if (response.value.status === 403) throw new ForbiddenError("User not allowed");
      if (response.value.status === 401) throw new UnauthorizedError("User not logged in");
      if (response.value.ok) {
        mailingList.value = data.value;
        initialMailingList.value = deepCopy(data.value);
        isNewList.value = false;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const initMailingList = () => {
    let expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 90);

    mailingList.value = { expired_at: expirationDate.toISOString() };
    initialMailingList.value = deepCopy(mailingList.value);
    isNewList.value = true;
  };

  const createMailingList = async () => {
    let formData = new FormData();
    formData.append("mailing_list", JSON.stringify(mailingList.value));
    formData.append("signed_blob_id", signedBlobId.value || "");
    formData.append("client_id", getClientId());

    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists/`).json().post(formData, null);

      if (response.value.ok) {
        mailingList.value = data.value;
        initialMailingList.value = deepCopy(data.value);

        captureEvent("mailing_list_created");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const updateMailingList = async () => {
    let formData = new FormData();
    formData.append("mailing_list", JSON.stringify(mailingList.value));
    formData.append("signed_blob_id", signedBlobId.value || "");
    formData.append("client_id", getClientId());
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists/${mailingList.value.token}`)
        .json()
        .patch(formData, null);

      if (response.value.ok) {
        mailingList.value = data.value;
        initialMailingList.value = deepCopy(data.value);

        captureEvent("mailing_list_updated");
      }
    } catch (err) {
      handleError(err);
    }
  };

  // file
  const getCsvFileName = computed(() => mailingListCsvFile.value?.name);
  const uploadContactsFile = async (file) => {
    try {
      const { uploadFile } = useDirectUpload(
        async (blob) => {
          signedBlobId.value = blob.signed_id;
          await updateListCsvFile(file);
        },
        (error) => {
          console.error("Direct upload error:", error);
        },
      );

      uploadFile(file);
    } catch (err) {
      handleError(err);
    }
  };

  const updateListCsvFile = async (value) => (mailingListCsvFile.value = value);
  const resetListCsvFile = () => {
    mailingListCsvFile.value = null;
    signedBlobId.value = null;
  };

  return {
    // item
    initialMailingList,
    mailingList,
    isNewList,
    hasListChanged,
    isListExpired,
    getMailingList,
    initMailingList,
    createMailingList,
    updateMailingList,
    // file
    mailingListCsvFile,
    getCsvFileName,
    uploadContactsFile,
    resetListCsvFile,
  };
});
